// Core domain types for the PulseAI Newsletter Management System.

export type NewsletterStatus = 'draft' | 'published';

export type ActionItemStatus = 'open' | 'in_progress' | 'blocked' | 'completed';

export type KpiTrend = 'up' | 'down' | 'flat';

/** The markdown-bodied narrative sections of a newsletter issue. */
export interface NewsletterSections {
  executiveSummary: string;
  keyAchievements: string;
  productUpdates: string;
  engineeringProgress: string;
  aiInnovations: string;
  customerSuccessStories: string;
  upcomingMilestones: string;
  risksChallenges: string;
  decisionsRequired: string;
  appendix: string;
}

export const SECTION_LABELS: Record<keyof NewsletterSections, string> = {
  executiveSummary: 'Executive Summary',
  keyAchievements: 'Key Achievements',
  productUpdates: 'Product Updates',
  engineeringProgress: 'Engineering Progress',
  aiInnovations: 'AI Innovations',
  customerSuccessStories: 'Customer Success Stories',
  upcomingMilestones: 'Upcoming Milestones',
  risksChallenges: 'Risks & Challenges',
  decisionsRequired: 'Decisions Required',
  appendix: 'Appendix',
};

export const SECTION_ORDER: (keyof NewsletterSections)[] = [
  'executiveSummary',
  'keyAchievements',
  'productUpdates',
  'engineeringProgress',
  'aiInnovations',
  'customerSuccessStories',
  'upcomingMilestones',
  'risksChallenges',
  'decisionsRequired',
  'appendix',
];

/** Every togglable content block in the newsletter template, including the two structured (non-Markdown) blocks. */
export type SectionKey = keyof NewsletterSections | 'kpis' | 'actionItems';

export const ALL_SECTION_LABELS: Record<SectionKey, string> = {
  executiveSummary: 'Executive Summary',
  keyAchievements: 'Key Achievements',
  productUpdates: 'Product Updates',
  engineeringProgress: 'Engineering Progress',
  aiInnovations: 'AI Innovations',
  customerSuccessStories: 'Customer Success Stories',
  kpis: 'Metrics & KPIs',
  upcomingMilestones: 'Upcoming Milestones',
  risksChallenges: 'Risks & Challenges',
  actionItems: 'Action Items',
  decisionsRequired: 'Decisions Required',
  appendix: 'Appendix',
};

/** Template order matching the functional spec, interleaving KPIs and Action Items among the narrative sections. */
export const ALL_SECTION_ORDER: SectionKey[] = [
  'executiveSummary',
  'keyAchievements',
  'productUpdates',
  'engineeringProgress',
  'aiInnovations',
  'customerSuccessStories',
  'kpis',
  'upcomingMilestones',
  'risksChallenges',
  'actionItems',
  'decisionsRequired',
  'appendix',
];

export interface KpiMetric {
  id: string;
  label: string;
  value: string;
  target?: string;
  trend?: KpiTrend;
}

export interface Newsletter {
  id: string;
  title: string;
  issueNumber: number;
  status: NewsletterStatus;
  periodStart: string; // ISO date
  periodEnd: string; // ISO date
  sections: NewsletterSections;
  kpis: KpiMetric[];
  /** Which content blocks appear in this issue's preview/export. Defaults to all when absent (older records). */
  enabledSections?: SectionKey[];
  coverImage?: string; // data URL
  currentVersion: number;
  createdAt: string; // ISO datetime
  updatedAt: string; // ISO datetime
  publishedAt?: string; // ISO datetime
}

/** A point-in-time snapshot of a newsletter, captured on publish or manual save. */
export interface NewsletterVersion {
  id: string;
  newsletterId: string;
  versionNumber: number;
  note?: string;
  snapshot: {
    title: string;
    sections: NewsletterSections;
    kpis: KpiMetric[];
    actionItemIds: string[];
    enabledSections: SectionKey[];
  };
  createdAt: string; // ISO datetime
}

export interface ActionItem {
  id: string;
  newsletterId: string;
  title: string;
  owner: string;
  dueDate: string; // ISO date
  status: ActionItemStatus;
  notes?: string;
  carriedFromId?: string;
  createdAt: string; // ISO datetime
  updatedAt: string; // ISO datetime
  completedAt?: string; // ISO datetime
}

export const ACTION_ITEM_STATUS_LABELS: Record<ActionItemStatus, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  blocked: 'Blocked',
  completed: 'Completed',
};

/** Resolves the effective enabled-section list, defaulting to "everything" for records saved before this field existed. */
export function getEnabledSections(newsletter: Pick<Newsletter, 'enabledSections'>): SectionKey[] {
  return newsletter.enabledSections ?? ALL_SECTION_ORDER;
}

export interface AsyncState {
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}
